import { useId, useState, type ReactNode } from 'react';
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  dayValue,
  hourValue,
  momentOf,
} from '@wundo/ui';
import type { MeetingTypeOption } from '@/lib/withCurrentTemplate.ts';
import { m } from '@/paraglide/messages.js';
import { defaultStart } from './manualMeetingTime.ts';
import {
  defaultMeetingTemplateId,
  meetingTypeIsAChoice,
  meetingTypeOptions,
} from './meetingTypeChoice.ts';

export interface ManualMeetingValues {
  title: string;
  startsAt: Date;
  templateId: string;
  /** Who it is with, by name. Empty whenever the question was not put. */
  attendees: string[];
}

/**
 * Whether this user is asked who the meeting is with, and the names worth
 * offering them. Null hides the question altogether — see the dialog below.
 */
export interface ManualMeetingPeople {
  suggestions: string[];
}

export interface ManualMeetingDialogProps {
  open: boolean;
  /** The words on the header and on the button that saves. */
  title: string;
  saveLabel: string;
  /**
   * What the fields open with. Empty for a meeting that does not exist yet —
   * `templateId` too, which is then filled in from the list below.
   */
  initial?: {
    title: string;
    startsAt: Date;
    templateId?: string;
    attendees?: string[];
  };
  /** Every gesprekstype this user may pick, in the order the picker shows them. */
  templates: MeetingTypeOption[];
  /** Null for a user whose calendar Wundo reads: no "Met wie?" at all. */
  withPeople: ManualMeetingPeople | null;
  pending: boolean;
  failed: boolean;
  onSubmit: (values: ManualMeetingValues) => void;
  onCancel: () => void;
}

/**
 * The one form behind every meeting a user writes down themselves: a title, a
 * day, an hour, the gesprekstype it follows, and — for somebody without a
 * calendar — who it is with. Both adding and editing use it, because both ask
 * exactly the same questions; a second form could only drift from this one.
 *
 * Who it is with used to belong in the title, because a name typed here had
 * nowhere to go. DEV-62 gave it one: a tagged name builds the team overview and
 * the timeline per person that a calendar would otherwise have built. So the
 * question is put to exactly the people it can help — `withPeople` null is a
 * user whose calendar already answers it, and their guests come off the invite.
 *
 * Still nothing else: how long a meeting runs is never asked, and a join link
 * or a notetaker a manual meeting simply does not have.
 *
 * The form is mounted when the dialog opens and thrown away when it closes, so
 * the fields start from the meeting rather than being pushed back to it. A box
 * still holding the previous meeting's title is how a rename becomes a
 * duplicate, and a form that only exists while it is on screen cannot do that.
 */
export function ManualMeetingDialog({
  open,
  title,
  saveLabel,
  initial,
  templates,
  withPeople,
  pending,
  failed,
  onSubmit,
  onCancel,
}: ManualMeetingDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent size='md'>
        {open && (
          <ManualMeetingForm
            title={title}
            saveLabel={saveLabel}
            initial={initial}
            templates={templates}
            withPeople={withPeople}
            pending={pending}
            failed={failed}
            onSubmit={onSubmit}
            onCancel={onCancel}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

type ManualMeetingFormProps = Omit<ManualMeetingDialogProps, 'open'>;

function ManualMeetingForm({
  title,
  saveLabel,
  initial,
  templates,
  withPeople,
  pending,
  failed,
  onSubmit,
  onCancel,
}: ManualMeetingFormProps) {
  const start = initial?.startsAt ?? defaultStart();
  const [name, setName] = useState(initial?.title ?? '');
  const [day, setDay] = useState(() => dayValue(start));
  const [hour, setHour] = useState(() => hourValue(start));
  /*
   * Held even where the field is hidden, and handed back untouched: a meeting
   * tagged before the calendar arrived must not lose its people to an edit that
   * never showed them.
   */
  const [attendees, setAttendees] = useState(() => initial?.attendees ?? []);
  const peopleId = useId();
  /*
   * Only what was answered: the type this meeting already has when it is being
   * edited, and otherwise nothing until the user picks one. The single type an
   * organisation may have is filled in below rather than stored here, because
   * the list is usually still loading when the dialog opens.
   */
  const [picked, setPicked] = useState(() => initial?.templateId ?? '');
  const templateId = picked || defaultMeetingTemplateId(templates);

  /*
   * With one gesprekstype there is nothing to ask: it is applied silently and
   * the dialog stays a title, a day and an hour. The dropdown is rendered from
   * two onwards, and then it has to be answered.
   */
  const asksForType = meetingTypeIsAChoice(templates);

  const startsAt = momentOf(day, hour);
  const submittable = name.trim().length > 0 && startsAt !== null && templateId !== '';

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>

      <div className='flex flex-col gap-4 px-5 py-4'>
        <Field label={m.manual_meeting_title_label()}>
          <Input
            aria-label={m.manual_meeting_title_label()}
            placeholder={m.manual_meeting_title_placeholder()}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        {/*
          Right under the title, because it is the same question continued:
          what the conversation is, and who it is with. Optional in as many
          words — a meeting with nobody named is still a meeting.
        */}
        {withPeople && (
          <div className='flex flex-col gap-1.5'>
            <label htmlFor={peopleId} className='text-sm font-semibold text-ink'>
              {m.manual_meeting_with_label()}{' '}
              <span className='font-normal text-ink-muted'>
                {m.manual_meeting_with_optional()}
              </span>
            </label>
            <Combobox
              id={peopleId}
              values={attendees}
              onValuesChange={setAttendees}
              suggestions={withPeople.suggestions}
              placeholder={m.manual_meeting_with_placeholder()}
              removeLabel={(person) => m.manual_meeting_with_remove({ name: person })}
            />
            <p className='text-xs text-ink-muted'>{m.manual_meeting_with_hint()}</p>
          </div>
        )}

        <div className='flex gap-3'>
          <div className='flex-1'>
            <Field label={m.manual_meeting_day_label()}>
              <Input
                type='date'
                aria-label={m.manual_meeting_day_label()}
                value={day}
                onChange={(event) => setDay(event.target.value)}
              />
            </Field>
          </div>
          <div className='flex-1'>
            <Field label={m.manual_meeting_hour_label()}>
              <Input
                type='time'
                aria-label={m.manual_meeting_hour_label()}
                value={hour}
                onChange={(event) => setHour(event.target.value)}
              />
            </Field>
          </div>
        </div>

        {asksForType && (
          <Field label={m.manual_meeting_type_label()}>
            <Select
              aria-label={m.manual_meeting_type_label()}
              wrapperClassName='w-full'
              className='w-full'
              value={templateId}
              options={meetingTypeOptions(templates, templateId)}
              onChange={(event) => setPicked(event.target.value)}
            />
          </Field>
        )}

        {failed && (
          <p role='alert' className='text-sm text-danger'>
            {m.manual_meeting_failed()}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button size='sm' variant='quiet' onClick={onCancel}>
          {m.manual_meeting_cancel()}
        </Button>
        <Button
          size='sm'
          disabled={!submittable || pending}
          onClick={() =>
            submittable &&
            onSubmit({
              title: name.trim(),
              startsAt: startsAt!,
              templateId,
              attendees,
            })
          }
        >
          {saveLabel}
        </Button>
      </DialogFooter>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className='flex flex-col gap-1.5'>
      <span className='text-sm font-semibold text-ink'>{label}</span>
      {children}
    </label>
  );
}
